import { useState } from 'react';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Badge, Button, Card, InsurerLogo, Pagination, Select } from '@/components/common';
import {
  useByInsurerReport,
  useByProductReport,
  useByPospReport,
  useLifecyclePipelineReport,
  useProfitabilityReport,
  useExportByInsurer,
  useExportByProduct,
  useExportByPosp,
  useExportLifecyclePipeline,
  usePoliciesByInsurer,
  useExportPoliciesByInsurer,
  useExportPoliciesByPosp,
} from '@/hooks/useCommissionReports';
import { usePOSPPolicies } from '@/hooks/useBrokers';
// The Insurer filter is a dropdown of the BROKER'S OWN insurers. `?insurer` is
// read server-side as a UUID or the insurer CODE and nothing else
// (CommissionReportsViewSet._apply_insurer_filter), so the free-text box that
// used to sit here — "Filter by insurer code or name" — matched nothing the
// moment anyone typed a name, and every report on this tab emptied without
// saying why. `useBrokerInsurers` is the switcher-aware, broker-scoped hook;
// `/insurers/insurers/lookup/` would offer every insurer on the platform.
import { useBrokerInsurers } from '@/hooks/useBrokerInsurers';
import { useAuthStore } from '@/store/auth';
import { formatCurrency, formatDate, titleCase } from '@/utils';
import { PolicyNumberCell } from './PolicyNumberCell';
import type { ReportFilters } from '@/services/commissionReports';
import type { BrokerContext, PayoutRegisterEntry, ProfitabilitySummary } from '@/types';
import { insurerPolicyMoneyColumns } from './reportMoneyColumns';

/** Which grouping row was clicked. `id` is the insurer id / the POSP id. */
type Drilldown = { kind: 'insurer' | 'posp'; id: string; name: string };

// ─── Tab + filter config ────────────────────────────────────────────────────

type ReportTabId = 'by-insurer' | 'by-product' | 'by-posp' | 'lifecycle' | 'profitability';

const reportTabs: { id: ReportTabId; label: string }[] = [
  { id: 'by-insurer', label: 'By Insurance Company' },
  { id: 'by-product', label: 'By Policy Type / Product' },
  { id: 'by-posp', label: 'By POSP (Agent)' },
  { id: 'lifecycle', label: 'Lifecycle & Payouts' },
];

// Human decision (item 5, round2): the profitability tab is visible to
// broker_admin ONLY — not broker_employee, not super_admin/country_admin, not
// posp. This mirrors the server-side gate on GET .../reports/profitability/
// (CommissionReportsViewSet.profitability, apps/commissions/views.py), which is
// deliberately STRICTER than the _BROKER_ROLES matrix every other report in this
// tab follows. Hiding the tab is a UX nicety, not the security boundary — the
// backend 403s regardless of what the client renders.
const profitabilityTab: { id: ReportTabId; label: string } = { id: 'profitability', label: 'Profitability' };

const periodOptions = [
  { value: 'all', label: 'All Time' },
  { value: 'monthly', label: 'This Month' },
  { value: 'fy', label: 'This FY (Apr-Mar)' },
];

// ─── ReportsTab ───────────────────────────────────────────────────────────────

export function ReportsTab() {
  const [activeReportTab, setActiveReportTab] = useState<ReportTabId>('by-insurer');
  const [period, setPeriod] = useState('all');
  const [insurer, setInsurer] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [insurerPage, setInsurerPage] = useState(1);
  const [productPage, setProductPage] = useState(1);
  const [pospPage, setPospPage] = useState(1);
  const [drilldown, setDrilldown] = useState<Drilldown | null>(null);

  // FE gate for the Profitability tab — broker_admin ONLY (see profitabilityTab
  // comment above). Not a security boundary by itself: the endpoint 403s any
  // other role regardless of this check, but there is no reason to render the
  // tab, or fire the request, for a role that can never see the data.
  const user = useAuthStore((s) => s.user);
  const isBrokerAdmin = user?.role === 'broker_admin';
  const visibleReportTabs = isBrokerAdmin ? [...reportTabs, profitabilityTab] : reportTabs;

  // Insurer options follow the BrokerSwitcher's current selection, not
  // `user.tenant_id`: a multi-broker user reporting on broker B must be offered
  // B's insurers. Fall back to tenant_id when there is no broker context
  // (single-broker users, non-broker logins) — the same expression the Ledger
  // tab uses.
  const loginType = useAuthStore((s) => s.loginType);
  const tenantContext = useAuthStore((s) => s.tenantContext);
  // The tenant_id fallback is gated on tenant_type === 'broker': that is the
  // server's own rule for which ids a caller may name (`_switchable_broker_ids`,
  // apps/brokers/views.py), and naming any other id earns a 403 instead of a list.
  const brokerId =
    (loginType === 'broker'
      ? (tenantContext as BrokerContext | null)?.id ?? undefined
      : undefined) ??
    (user?.tenant_type === 'broker' ? user?.tenant_id ?? undefined : undefined);
  const { data: insurerOptions } = useBrokerInsurers(brokerId);
  const insurerSelectOptions = [
    { value: '', label: 'All Insurers' },
    ...(insurerOptions ?? []).map((i) => ({ value: i.id, label: i.name })),
  ];

  // One filter state drives all four reports AND the drill-downs, so the same
  // question is asked everywhere. An explicit date window OVERRIDES ?period
  // server-side (they are not intersected), so the Period control is disabled
  // while a window is set rather than quietly doing nothing.
  const hasDateRange = Boolean(dateFrom || dateTo);
  const filters: ReportFilters = {
    period: hasDateRange ? undefined : period || undefined,
    insurer: insurer || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  };

  const resetPages = () => {
    setInsurerPage(1);
    setProductPage(1);
    setPospPage(1);
  };

  // Queries
  const byInsurerQuery = useByInsurerReport(insurerPage, filters);
  const byProductQuery = useByProductReport(productPage, filters);
  const byPospQuery = useByPospReport(pospPage, filters);
  const lifecycleQuery = useLifecyclePipelineReport(filters);
  // Only ever fetched for a broker_admin — see isBrokerAdmin above.
  const profitabilityQuery = useProfitabilityReport(filters, isBrokerAdmin);

  // Export mutations
  const exportInsurer = useExportByInsurer();
  const exportProduct = useExportByProduct();
  const exportPosp = useExportByPosp();
  const exportLifecycle = useExportLifecyclePipeline();

  const handlePeriodChange = (v: string) => {
    setPeriod(v);
    resetPages();
  };

  const handleInsurerChange = (v: string) => {
    setInsurer(v);
    resetPages();
  };

  const handleDateChange = (which: 'from' | 'to', v: string) => {
    if (which === 'from') setDateFrom(v);
    else setDateTo(v);
    resetPages();
  };

  return (
    <div>
      {/* Shared filters — they narrow the grouping reports AND the open drill-down */}
      <Card className="mb-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-4">
          <div>
            <Select
              label="Period"
              options={periodOptions}
              value={period}
              onChange={(e) => handlePeriodChange(e.target.value)}
              disabled={hasDateRange}
              helperText={hasDateRange ? 'Overridden by the date range' : undefined}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              From (Optional)
            </label>
            <input
              type="date"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => handleDateChange('from', e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              To (Optional)
            </label>
            <input
              type="date"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => handleDateChange('to', e.target.value)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            {/* A dropdown, not a text box: the value posted as `?insurer` has to
                be an id (or an exact code), so anything typed by hand matched
                nothing and emptied every report on this tab. */}
            <Select
              id="reports-insurer"
              label="Insurer (Optional)"
              options={insurerSelectOptions}
              value={insurer}
              onChange={(e) => handleInsurerChange(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {/* A drill-down replaces the tab strip: it is a view OF the row that was
          clicked, and the shared filters above still narrow it. */}
      {drilldown ? (
        drilldown.kind === 'insurer' ? (
          <InsurerPoliciesDrilldown
            insurerId={drilldown.id}
            insurerName={drilldown.name}
            filters={filters}
            onBack={() => setDrilldown(null)}
          />
        ) : (
          <PospPoliciesDrilldown
            pospId={drilldown.id}
            pospName={drilldown.name}
            filters={filters}
            onBack={() => setDrilldown(null)}
          />
        )
      ) : (
        <>
      {/* Report tabs */}
      <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
        <nav className="flex flex-wrap gap-x-6">
          {visibleReportTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveReportTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeReportTab === tab.id
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* By Insurer Tab */}
      {activeReportTab === 'by-insurer' && (
        <ByInsurerReportTab
          data={byInsurerQuery.data}
          isLoading={byInsurerQuery.isLoading}
          page={insurerPage}
          onPageChange={setInsurerPage}
          onExport={() => exportInsurer.mutate(filters)}
          exporting={exportInsurer.isPending}
          onDrilldown={(id, name) => setDrilldown({ kind: 'insurer', id, name })}
        />
      )}

      {/* By Product Tab */}
      {activeReportTab === 'by-product' && (
        <ByProductReportTab
          data={byProductQuery.data}
          isLoading={byProductQuery.isLoading}
          page={productPage}
          onPageChange={setProductPage}
          onExport={() => exportProduct.mutate(filters)}
          exporting={exportProduct.isPending}
        />
      )}

      {/* By POSP Tab */}
      {activeReportTab === 'by-posp' && (
        <ByPospReportTab
          data={byPospQuery.data}
          isLoading={byPospQuery.isLoading}
          page={pospPage}
          onPageChange={setPospPage}
          onExport={() => exportPosp.mutate(filters)}
          exporting={exportPosp.isPending}
          onDrilldown={(id, name) => setDrilldown({ kind: 'posp', id, name })}
        />
      )}

      {/* Lifecycle + Payouts Tab */}
      {activeReportTab === 'lifecycle' && (
        <LifecyclePayoutTab
          data={lifecycleQuery.data}
          isLoading={lifecycleQuery.isLoading}
          onExport={() => exportLifecycle.mutate(filters)}
          exporting={exportLifecycle.isPending}
        />
      )}

      {/* Profitability Tab — broker_admin only, see isBrokerAdmin above */}
      {activeReportTab === 'profitability' && isBrokerAdmin && (
        <ProfitabilityTab data={profitabilityQuery.data} isLoading={profitabilityQuery.isLoading} />
      )}
        </>
      )}
    </div>
  );
}

// ─── By Insurer Report Tab ─────────────────────────────────────────────────

function ByInsurerReportTab({
  data,
  isLoading,
  page,
  onPageChange,
  onExport,
  exporting,
  onDrilldown,
}: {
  data: ReturnType<typeof useByInsurerReport>['data'];
  isLoading: boolean;
  page: number;
  onPageChange: (p: number) => void;
  onExport: () => void;
  exporting: boolean;
  onDrilldown: (insurerId: string, insurerName: string) => void;
}) {
  const rows = data?.results ?? [];
  const summary = data?.summary;
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / 20) || 1;

  return (
    <>
      {summary && (
        <div className="grid grid-cols-2 gap-4 mb-6 sm:grid-cols-5">
          <SummaryCard label="Total Received" value={formatCurrency(summary.total_received)} color="text-green-600 dark:text-green-400" />
          <SummaryCard label="Total Given" value={formatCurrency(summary.total_given)} color="text-orange-600 dark:text-orange-400" />
          <SummaryCard label="Total Margin" value={formatCurrency(summary.total_margin)} color="text-blue-600 dark:text-blue-400" />
          <SummaryCard label="Policies" value={String(summary.total_policies)} color="text-gray-700 dark:text-gray-300" />
          <SummaryCard label="Premium" value={formatCurrency(summary.total_premium)} color="text-gray-900 dark:text-white" />
        </div>
      )}

      <div className="flex justify-end mb-4">
        <Button onClick={onExport} isLoading={exporting} size="sm">
          Export CSV
        </Button>
      </div>

      <Card padding="none">
        {isLoading ? (
          <TableSkeleton cols={6} />
        ) : rows.length === 0 ? (
          <EmptyState message="No data available for the selected filters." />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    {['Insurer', 'Received', 'Given', 'Margin', 'Policies', 'Premium'].map((h) => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.map((row) => (
                    <tr key={row.id || row.insurer_code} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                        {/* The "Direct / Unknown" bucket has no insurer id, so it has
                            nothing to drill into. */}
                        {row.id ? (
                          // U-05: the insurer's logo beside its name. The
                          // Direct / Unknown bucket below is no company, so it
                          // gets none.
                          <span className="flex items-center gap-2">
                            <InsurerLogo src={row.insurer_logo_url} name={row.insurer_name} size="xs" />
                            <button
                              type="button"
                              onClick={() => onDrilldown(row.id!, row.insurer_name)}
                              className="text-primary-600 hover:text-primary-700 dark:text-primary-400 hover:underline text-left"
                            >
                              {row.insurer_name}
                            </button>
                          </span>
                        ) : (
                          row.insurer_name
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-green-600 dark:text-green-400 font-medium">{formatCurrency(row.received)}</td>
                      <td className="px-6 py-4 text-sm text-orange-600 dark:text-orange-400 font-medium">{formatCurrency(row.given)}</td>
                      <td className="px-6 py-4 text-sm text-blue-600 dark:text-blue-400 font-medium">{formatCurrency(row.margin)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{row.policy_count}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{formatCurrency(row.premium)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems} itemsPerPage={20} onPageChange={onPageChange} />
            )}
          </>
        )}
      </Card>
    </>
  );
}

// ─── By Product Report Tab ──────────────────────────────────────────────────

function ByProductReportTab({
  data,
  isLoading,
  page,
  onPageChange,
  onExport,
  exporting,
}: {
  data: ReturnType<typeof useByProductReport>['data'];
  isLoading: boolean;
  page: number;
  onPageChange: (p: number) => void;
  onExport: () => void;
  exporting: boolean;
}) {
  const rows = data?.results ?? [];
  const summary = data?.summary;
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / 20) || 1;

  return (
    <>
      {summary && (
        <div className="grid grid-cols-2 gap-4 mb-6 sm:grid-cols-5">
          <SummaryCard label="Total Received" value={formatCurrency(summary.total_received)} color="text-green-600 dark:text-green-400" />
          <SummaryCard label="Total Given" value={formatCurrency(summary.total_given)} color="text-orange-600 dark:text-orange-400" />
          <SummaryCard label="Total Margin" value={formatCurrency(summary.total_margin)} color="text-blue-600 dark:text-blue-400" />
          <SummaryCard label="Policies" value={String(summary.total_policies)} color="text-gray-700 dark:text-gray-300" />
          <SummaryCard label="Premium" value={formatCurrency(summary.total_premium)} color="text-gray-900 dark:text-white" />
        </div>
      )}

      <div className="flex justify-end mb-4">
        <Button onClick={onExport} isLoading={exporting} size="sm">
          Export CSV
        </Button>
      </div>

      <Card padding="none">
        {isLoading ? (
          <TableSkeleton cols={7} />
        ) : rows.length === 0 ? (
          <EmptyState message="No data available for the selected filters." />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    {['Product', 'Type', 'Received', 'Given', 'Margin', 'Policies', 'Premium'].map((h) => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.map((row) => (
                    <tr key={row.id || `${row.product_name}-${row.policy_type}`} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{row.product_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{row.policy_type}</td>
                      <td className="px-6 py-4 text-sm text-green-600 dark:text-green-400 font-medium">{formatCurrency(row.received)}</td>
                      <td className="px-6 py-4 text-sm text-orange-600 dark:text-orange-400 font-medium">{formatCurrency(row.given)}</td>
                      <td className="px-6 py-4 text-sm text-blue-600 dark:text-blue-400 font-medium">{formatCurrency(row.margin)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{row.policy_count}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{formatCurrency(row.premium)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems} itemsPerPage={20} onPageChange={onPageChange} />
            )}
          </>
        )}
      </Card>
    </>
  );
}

// ─── By POSP Report Tab ─────────────────────────────────────────────────────

function ByPospReportTab({
  data,
  isLoading,
  page,
  onPageChange,
  onExport,
  exporting,
  onDrilldown,
}: {
  data: ReturnType<typeof useByPospReport>['data'];
  isLoading: boolean;
  page: number;
  onPageChange: (p: number) => void;
  onExport: () => void;
  exporting: boolean;
  onDrilldown: (pospId: string, pospName: string) => void;
}) {
  const rows = data?.results ?? [];
  const summary = data?.summary;
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / 20) || 1;

  return (
    <>
      {summary && (
        <div className="grid grid-cols-2 gap-4 mb-6 sm:grid-cols-4">
          <SummaryCard label="Total Earnings" value={formatCurrency(summary.total_earnings)} color="text-green-600 dark:text-green-400" />
          <SummaryCard label="Total Policies" value={String(summary.total_policies)} color="text-gray-700 dark:text-gray-300" />
          <SummaryCard label="Total Paid" value={formatCurrency(summary.total_paid)} color="text-blue-600 dark:text-blue-400" />
          <SummaryCard label="Total Pending" value={formatCurrency(summary.total_pending)} color="text-orange-600 dark:text-orange-400" />
        </div>
      )}

      <div className="flex justify-end mb-4">
        <Button onClick={onExport} isLoading={exporting} size="sm">
          Export CSV
        </Button>
      </div>

      <Card padding="none">
        {isLoading ? (
          <TableSkeleton cols={6} />
        ) : rows.length === 0 ? (
          <EmptyState message="No agents found for the selected filters." />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    {['Agent (POSP)', 'Earnings', 'Policies', 'Paid', 'Pending', 'Status'].map((h) => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.map((row) => (
                    <tr key={row.id || row.posp_code} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">
                        {row.id ? (
                          <button
                            type="button"
                            onClick={() => onDrilldown(row.id!, row.posp_name)}
                            className="text-primary-600 hover:text-primary-700 dark:text-primary-400 hover:underline text-left"
                          >
                            {row.posp_name}
                          </button>
                        ) : (
                          row.posp_name
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-green-600 dark:text-green-400 font-medium">{formatCurrency(row.earnings)}</td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{row.policy_count}</td>
                      <td className="px-6 py-4 text-sm text-blue-600 dark:text-blue-400 font-medium">{formatCurrency(row.paid)}</td>
                      <td className="px-6 py-4 text-sm text-orange-600 dark:text-orange-400 font-medium">{formatCurrency(row.pending)}</td>
                      <td className="px-6 py-4">
                        <Badge variant={getPaymentStatusVariant(row.payment_status)} size="sm">
                          {titleCase(row.payment_status)}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems} itemsPerPage={20} onPageChange={onPageChange} />
            )}
          </>
        )}
      </Card>
    </>
  );
}

// ─── By Insurer → drill-down: one insurer's policies ────────────────────────

function InsurerPoliciesDrilldown({
  insurerId,
  insurerName,
  filters,
  onBack,
}: {
  insurerId: string;
  insurerName: string;
  filters: ReportFilters;
  onBack: () => void;
}) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = usePoliciesByInsurer(insurerId, page, filters);
  const exportPolicies = useExportPoliciesByInsurer();

  const rows = data?.results ?? [];
  const summary = data?.summary;
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / 20) || 1;

  // The columns come from the payload, never from an assumption about the caller:
  // a POSP's rows carry ONLY `your_commission` (see reportMoneyColumns).
  const moneyColumns = insurerPolicyMoneyColumns(rows);

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" leftIcon={<ArrowLeftIcon className="w-4 h-4" />} onClick={onBack}>
            Back to insurers
          </Button>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{insurerName}</h3>
        </div>
        <Button
          size="sm"
          isLoading={exportPolicies.isPending}
          onClick={() => exportPolicies.mutate({ insurerId, filters })}
        >
          Export CSV
        </Button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 gap-4 mb-6 sm:grid-cols-5">
          {/* Each card is rendered only if the server sent that figure. */}
          {summary.received !== undefined && (
            <SummaryCard label="Received" value={formatCurrency(summary.received)} color="text-green-600 dark:text-green-400" />
          )}
          {summary.paid_to_posp !== undefined && (
            <SummaryCard label="Paid to POSP" value={formatCurrency(summary.paid_to_posp)} color="text-orange-600 dark:text-orange-400" />
          )}
          {summary.paid_to_advertiser !== undefined && (
            <SummaryCard label="Paid to Advertiser" value={formatCurrency(summary.paid_to_advertiser)} color="text-orange-600 dark:text-orange-400" />
          )}
          {summary.margin !== undefined && (
            <SummaryCard label="Margin" value={formatCurrency(summary.margin)} color="text-blue-600 dark:text-blue-400" />
          )}
          {summary.your_commission !== undefined && (
            <SummaryCard label="Your Commission" value={formatCurrency(summary.your_commission)} color="text-green-600 dark:text-green-400" />
          )}
          {summary.downline_commission !== undefined && (
            <SummaryCard label="Downline Commission" value={formatCurrency(summary.downline_commission)} color="text-blue-600 dark:text-blue-400" />
          )}
          <SummaryCard label="Policies" value={String(summary.policy_count)} color="text-gray-900 dark:text-white" />
        </div>
      )}

      <Card padding="none">
        {isLoading ? (
          <TableSkeleton cols={5 + moneyColumns.length} />
        ) : rows.length === 0 ? (
          <EmptyState message="No policies for this insurer in the selected window." />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    {['Policy', 'Holder', 'POSP', 'Date', 'Premium', ...moneyColumns.map((c) => c.label)].map((h) => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.map((row, idx) => (
                    <tr key={row.id ?? `${row.policy_number}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-6 py-4 text-sm">
                        <PolicyNumberCell
                          policyNumber={row.policy_number}
                          insurerPolicyNumber={row.insurer_policy_number}
                        />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{row.holder_name || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{row.posp_name || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {row.policy_date ? formatDate(row.policy_date) : '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{formatCurrency(row.gross_premium)}</td>
                      {moneyColumns.map((col) => (
                        <td key={col.key} className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {formatCurrency(row[col.key] ?? '0')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems} itemsPerPage={20} onPageChange={setPage} />
            )}
          </>
        )}
      </Card>
    </>
  );
}

// ─── By POSP → drill-down: one agent's policies ─────────────────────────────
// Reuses the pre-existing GET /brokers/posps/{id}/policies/ (role-gated commission
// block per row) — there is no second backend for this.

function PospPoliciesDrilldown({
  pospId,
  pospName,
  filters,
  onBack,
}: {
  pospId: string;
  pospName: string;
  filters: ReportFilters;
  onBack: () => void;
}) {
  const [page, setPage] = useState(1);
  // The endpoint behind this drill-down (GET /brokers/posps/{id}/policies/)
  // honours ?period AND ?date_from/?date_to — the same windows as the by-insurer
  // drill-down — so the shared filter narrows this table too. Only ?insurer is
  // not a POSP-drilldown filter; it passes through unused.
  const { data, isLoading } = usePOSPPolicies(pospId, page, filters);
  const exportPolicies = useExportPoliciesByPosp();

  const rows = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / 20) || 1;

  // The commission block is role-gated exactly like the insurer drill-down: a
  // broker gets received/paid_to_posp/margin, a POSP gets only your_commission.
  // Derive the columns from the payload rather than assuming the broker shape.
  const has = (key: 'received' | 'paid_to_posp' | 'margin' | 'your_commission') =>
    rows.some((r) => r.commission?.[key] !== undefined);
  const moneyColumns = (
    [
      { key: 'received', label: 'Received' },
      { key: 'paid_to_posp', label: 'To POSP' },
      { key: 'margin', label: 'Margin' },
      { key: 'your_commission', label: 'Your Commission' },
    ] as const
  ).filter((c) => has(c.key));

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" leftIcon={<ArrowLeftIcon className="w-4 h-4" />} onClick={onBack}>
            Back to agents
          </Button>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{pospName}</h3>
        </div>
        <Button
          size="sm"
          isLoading={exportPolicies.isPending}
          onClick={() => exportPolicies.mutate({ pospId, filters })}
        >
          Export CSV
        </Button>
      </div>

      <Card padding="none">
        {isLoading ? (
          <TableSkeleton cols={5 + moneyColumns.length} />
        ) : rows.length === 0 ? (
          <EmptyState message="This agent has no policies in the selected window." />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    {['Policy', 'Holder', 'Insurer', 'Date', 'Premium', ...moneyColumns.map((c) => c.label)].map((h) => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {rows.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-6 py-4 text-sm">
                        <PolicyNumberCell
                          policyNumber={row.policy_number}
                          insurerPolicyNumber={row.insurer_policy_number}
                        />
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{row.holder_name || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{row.insurer_name || '—'}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {row.policy_date ? formatDate(row.policy_date) : '—'}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">{formatCurrency(row.total_premium)}</td>
                      {moneyColumns.map((col) => (
                        <td key={col.key} className="px-6 py-4 text-sm text-gray-900 dark:text-gray-100">
                          {formatCurrency(row.commission?.[col.key] ?? '0')}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination currentPage={page} totalPages={totalPages} totalItems={totalItems} itemsPerPage={20} onPageChange={setPage} />
            )}
          </>
        )}
      </Card>
    </>
  );
}

// ─── Lifecycle + Payout Register Tab ────────────────────────────────────────

type PayoutRegisterRow = PayoutRegisterEntry & { payment_modes?: string };

function LifecyclePayoutTab({
  data,
  isLoading,
  onExport,
  exporting,
}: {
  data: ReturnType<typeof useLifecyclePipelineReport>['data'];
  isLoading: boolean;
  onExport: () => void;
  exporting: boolean;
}) {
  const pipeline = data?.pipeline ?? [];
  /**
   * #253 — `payment_modes` is served by GET commissions/reports/lifecycle/ (it is
   * in `payout_register_columns_for`, so the JSON keys and the CSV header both
   * carry it) but is not yet on the shared `PayoutRegisterEntry` type, because
   * src/types/index.ts is outside this lane's ownership. Widened locally so the
   * column is real today; delete the intersection once the field lands upstream.
   *
   * A register row is AGGREGATED per POSP and period, so this is a joined list —
   * "Bank Transfer, By Cash" when a period held both — not a single mode.
   */
  const payoutRegister: PayoutRegisterRow[] = data?.payout_register ?? [];

  return (
    <>
      {/* Pipeline Overview */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Commission Pipeline by Status
        </h3>
        {pipeline.length === 0 ? (
          <Card className="mb-6">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No pipeline data available for the selected filters.
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-2 gap-4 mb-6 sm:grid-cols-3 lg:grid-cols-6">
            {pipeline.map((stage) => (
              <Card key={stage.stage}>
                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  {titleCase(stage.stage)}
                </p>
                <p className="mt-2 text-lg font-bold text-gray-900 dark:text-white">
                  {formatCurrency(stage.amount)}
                </p>
                <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                  {stage.count} entries
                </p>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Payout Register Table */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Month-End Payout Register
          </h3>
          <Button onClick={onExport} isLoading={exporting} size="sm">
            Export CSV
          </Button>
        </div>

        <Card padding="none">
          {isLoading ? (
            <TableSkeleton cols={8} />
          ) : payoutRegister.length === 0 ? (
            <EmptyState message="No payout records found for the selected period." />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-800">
                  <tr>
                    {['Agent', 'Period Start', 'Period End', 'Mode', 'Gross', 'TDS', 'GST', 'Net'].map((h) => (
                      <th key={h} className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {payoutRegister.map((entry, idx) => (
                    <tr key={`${entry.posp_code}-${idx}`} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{entry.posp_name}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{formatDate(entry.period_start)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{formatDate(entry.period_end)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{entry.payment_modes || '—'}</td>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-gray-100">{formatCurrency(entry.gross_amount)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{formatCurrency(entry.tds_amount)}</td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{formatCurrency(entry.gst_amount)}</td>
                      <td className="px-6 py-4 text-sm font-bold text-gray-900 dark:text-gray-100">{formatCurrency(entry.net_amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

// ─── Profitability Tab (broker_admin only) ──────────────────────────────────
//
// "Is the company making money?" — Received (from insurers) vs. Paid Out (to
// POSPs, the platform, and advertisers) vs. Margin (what the broker keeps).
// Backed 1:1 by GET .../reports/profitability/, which is a thin wrapper over
// the existing commission_summary_for/margin_sum() aggregation (visibility.py) —
// no commission math lives here or in the view, only display.

function ProfitabilityTab({
  data,
  isLoading,
}: {
  data: ProfitabilitySummary | undefined;
  isLoading: boolean;
}) {
  return (
    <div>
      <p className="mb-4 text-sm text-gray-500 dark:text-gray-400">
        Total commission received from insurers vs. total paid out (POSPs, platform,
        super-POSP overrides, and advertiser payouts) for the selected period. Margin is
        what the company actually keeps.
      </p>
      {isLoading ? (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Card key={i}>
              <div className="animate-pulse">
                <div className="h-3 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
                <div className="mt-3 h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            </Card>
          ))}
        </div>
      ) : !data ? (
        <EmptyState message="No profitability data available for the selected filters." />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <SummaryCard label="Received" value={formatCurrency(data.received)} color="text-green-600 dark:text-green-400" />
          <SummaryCard label="Paid Out" value={formatCurrency(data.paid_out)} color="text-orange-600 dark:text-orange-400" />
          <SummaryCard label="Margin (Profit)" value={formatCurrency(data.margin)} color="text-blue-600 dark:text-blue-400" />
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function SummaryCard({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: string;
}) {
  return (
    <Card>
      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
      <p className={`mt-2 text-xl font-bold ${color}`}>{value}</p>
    </Card>
  );
}

function TableSkeleton({ cols }: { cols: number }) {
  return (
    <div className="animate-pulse">
      <div className="h-12 bg-gray-100 dark:bg-gray-800" />
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="h-16 border-t border-gray-100 dark:border-gray-700 flex items-center px-6 gap-4"
        >
          {[...Array(cols)].map((_, j) => (
            <div key={j} className="flex-1 h-4 bg-gray-200 dark:bg-gray-700 rounded" />
          ))}
        </div>
      ))}
    </div>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="text-center py-12">
      <div className="mx-auto h-12 w-12 text-gray-400">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"
          />
        </svg>
      </div>
      <h3 className="mt-4 text-sm font-semibold text-gray-900 dark:text-gray-100">{message}</h3>
    </div>
  );
}

function getPaymentStatusVariant(
  status: 'fully_paid' | 'partially_paid' | 'pending',
): 'success' | 'warning' | 'default' {
  switch (status) {
    case 'fully_paid':
      return 'success';
    case 'partially_paid':
      return 'warning';
    case 'pending':
    default:
      return 'default';
  }
}
